import React, { useEffect, useId, useRef, useState } from "react";
import {
  STRATEGY_REVIEW_ACCESS_MESSAGES,
  STRATEGY_REVIEW_ACCESS_OUTCOME,
  STRATEGY_REVIEW_CODE_MAX_LENGTH,
  STRATEGY_REVIEW_CONTACT_MAX_LENGTH,
  describeStrategyReviewAccessFailure,
  forgetStrategyReviewRequest,
  readStrategyReviewRequest,
  requestStrategyReviewAccessCode,
  sweepExpiredStrategyReviewRequests,
  verifyStrategyReviewAccessCode,
} from "../utils/strategyReviewAccess";
import { US_REVIEW_MOBILE_ERROR, usReviewMobileContact, usReviewMobileFieldValue } from "../utils/strategyReviewMobileInput";
import "./StrategyReviewAccessPanel.css";

/*
 * Request → contact → code. The panel is offered by the page (an anonymous
 * reader of a withheld, reviewable strategy); it only collects what the
 * reader types and asks the backend.
 */
const STEP = Object.freeze({ REQUEST: "request", CONTACT: "contact", CODE: "code" });

/*
 * One heading and one line per step, and nothing else in words: the status
 * card above already says where the strategy and the campaign stand. The line
 * on C is the same for every request, whatever the backend did with it.
 */
const VERIFY_LINE = "Verify your email or mobile number to securely access the campaign strategy.";
const STEP_COPY = Object.freeze({
  [STEP.REQUEST]: { title: "Review this strategy", line: VERIFY_LINE },
  [STEP.CONTACT]: { title: "Review this strategy", line: VERIFY_LINE },
  [STEP.CODE]: { title: "Enter your access code", line: STRATEGY_REVIEW_ACCESS_MESSAGES.requested },
});

const resendAtOf = (pending) =>
  pending ? pending.requestedAt + pending.resendAfterSeconds * 1000 : 0;

// A request this browser made in the last fifteen minutes — for instance
// before the reader opened the code's email — resumes at the code.
const initialFlow = (campaignId) => {
  sweepExpiredStrategyReviewRequests();
  const pending = readStrategyReviewRequest(campaignId);
  return pending
    ? { step: STEP.CODE, contact: pending.contact, resendAt: resendAtOf(pending) }
    : { step: STEP.REQUEST, contact: "", resendAt: 0 };
};

/**
 * Strategy review access, beneath the withheld notice of the public strategy
 * microsite: the page's one action, so it is the panel the eye lands on.
 *
 *   A  "Review this strategy" and one primary button, "Request review access";
 *   B  Mobile (+1) or Email → "Send code", or "I already have a code";
 *   C  "Enter your access code", the same sentence for every request, the
 *      code, "View strategy" beneath it; "Send another code" once the
 *      cooldown has passed, and a way back to B;
 *   D  a verified code: the browser keeps the backend's HttpOnly grant cookie
 *      ("Remember this device for 30 days", checked unless the reader clears
 *      it; otherwise for the browser session), the pending request is
 *      dropped, and `onUnlocked(campaignId)` asks the page to read the
 *      strategy again.
 *
 * Nothing shown ever depends on whether the contact is known.
 */
export default function StrategyReviewAccessPanel({ apiBaseUrl, campaignId, onUnlocked }) {
  const [flow, setFlow] = useState(() => initialFlow(campaignId));
  const [contactMethod, setContactMethod] = useState(() => flow.contact.includes("@") ? "EMAIL" : "MOBILE");
  const [contactInput, setContactInput] = useState(() => usReviewMobileFieldValue(flow.contact));
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [clockTick, setClockTick] = useState(0);
  const inFlightRef = useRef(null);
  const movedRef = useRef(false);
  const contactRef = useRef(null);
  const codeRef = useRef(null);
  const idBase = useId();
  const titleId = `${idBase}-title`;
  const lineId = `${idBase}-line`;
  const contactId = `${idBase}-contact`;
  const countryId = `${idBase}-country`;
  const codeId = `${idBase}-code`;
  const rememberId = `${idBase}-remember`;
  const errorId = `${idBase}-error`;
  const copy = STEP_COPY[flow.step];

  // A call still in flight when the panel goes away is abandoned.
  useEffect(() => () => inFlightRef.current?.abort(), []);

  // Focus follows the reader's own moves — never the page load.
  useEffect(() => {
    if (!movedRef.current) return;
    movedRef.current = false;
    if (flow.step === STEP.CONTACT) contactRef.current?.focus();
    if (flow.step === STEP.CODE) codeRef.current?.focus();
  }, [flow.step, contactMethod]);

  // "Send another code" waits out the cooldown the request answered with.
  const resendReady = flow.step === STEP.CODE && Date.now() >= flow.resendAt;
  useEffect(() => {
    if (flow.step !== STEP.CODE) return undefined;
    const wait = flow.resendAt - Date.now();
    if (wait <= 0) return undefined;
    const timer = window.setTimeout(() => setClockTick((tick) => tick + 1), wait);
    return () => window.clearTimeout(timer);
  }, [flow.step, flow.resendAt, clockTick]);

  const isBusy = Boolean(busy);

  const call = async (kind, perform) => {
    if (inFlightRef.current) return null;
    const controller = new AbortController();
    inFlightRef.current = controller;
    setBusy(kind);
    setError("");
    const result = await perform(controller.signal);
    if (controller.signal.aborted) return null;
    inFlightRef.current = null;
    setBusy("");
    return result;
  };

  const moveTo = (nextFlow) => {
    movedRef.current = true;
    setError("");
    setCode("");
    setFlow(nextFlow);
  };

  const openContact = () => moveTo({ step: STEP.CONTACT, contact: "", resendAt: 0 });

  const enteredContact = () => {
    const contact = contactMethod === "MOBILE" ? usReviewMobileContact(contactInput) : contactInput.trim();
    if (!contact) {
      setError(contactMethod === "MOBILE" ? US_REVIEW_MOBILE_ERROR : STRATEGY_REVIEW_ACCESS_MESSAGES.invalidContact);
      contactRef.current?.focus();
    }
    return contact;
  };

  const sendCode = async (event) => {
    event.preventDefault();
    const contact = enteredContact();
    if (!contact) return;
    const result = await call("send", (signal) =>
      requestStrategyReviewAccessCode({ apiBaseUrl, campaignId, contact, signal })
    );
    if (!result) return;
    if (result.outcome === STRATEGY_REVIEW_ACCESS_OUTCOME.REQUESTED) {
      moveTo({ step: STEP.CODE, contact, resendAt: resendAtOf(result.pending) });
      return;
    }
    setError(result.outcome === STRATEGY_REVIEW_ACCESS_OUTCOME.INVALID_CONTACT && contactMethod === "MOBILE" ? US_REVIEW_MOBILE_ERROR : describeStrategyReviewAccessFailure(result));
    contactRef.current?.focus();
  };

  // Nothing is sent: the reader already holds a code for this contact.
  const enterExistingCode = () => {
    const contact = enteredContact();
    if (!contact) return;
    moveTo({ step: STEP.CODE, contact, resendAt: 0 });
  };

  const sendAnotherCode = async () => {
    const result = await call("resend", (signal) =>
      requestStrategyReviewAccessCode({ apiBaseUrl, campaignId, contact: flow.contact, signal })
    );
    if (!result) return;
    if (result.outcome === STRATEGY_REVIEW_ACCESS_OUTCOME.REQUESTED) {
      setFlow((current) => ({ ...current, resendAt: resendAtOf(result.pending) }));
      codeRef.current?.focus();
      return;
    }
    setError(describeStrategyReviewAccessFailure(result));
  };

  // Abandoning a contact abandons its pending request.
  const changeContact = () => {
    forgetStrategyReviewRequest(campaignId);
    setContactMethod(flow.contact.includes("@") ? "EMAIL" : "MOBILE");
    setContactInput(usReviewMobileFieldValue(flow.contact));
    moveTo({ step: STEP.CONTACT, contact: "", resendAt: 0 });
  };

  const viewStrategy = async (event) => {
    event.preventDefault();
    const submitted = code.trim();
    if (!submitted) return;
    const result = await call("verify", (signal) =>
      verifyStrategyReviewAccessCode({
        apiBaseUrl,
        campaignId,
        contact: flow.contact,
        code: submitted,
        remember,
        signal,
      })
    );
    if (!result) return;
    if (result.outcome === STRATEGY_REVIEW_ACCESS_OUTCOME.GRANTED) {
      setCode("");
      if (typeof onUnlocked === "function") onUnlocked(campaignId);
      return;
    }
    setError(describeStrategyReviewAccessFailure(result));
    codeRef.current?.focus();
  };

  const errorLine = error ? (
    <p id={errorId} className="strategy-review-access-error" role="alert">
      {error}
    </p>
  ) : null;

  return (
    <section
      className="strategy-review-access-panel"
      data-testid="strategy-review-access"
      data-step={flow.step}
      aria-labelledby={titleId}
    >
      <h2 id={titleId} className="strategy-review-access-title">
        {copy.title}
      </h2>
      {/*
        One live line across the steps, so arriving at C announces its
        sentence instead of only the field that focus lands on.
      */}
      <p id={lineId} className="strategy-review-access-note" role="status">
        {copy.line}
      </p>

      {flow.step === STEP.REQUEST ? (
        <button type="button" className="strategy-review-access-primary" onClick={openContact}>
          Request review access
        </button>
      ) : null}

      {/*
        No input has a name and no form posts anywhere of its own accord, so
        even a submission that bypassed its handler could not put what was
        typed into an address.
      */}
      {flow.step === STEP.CONTACT ? (
        <>
          <form className="strategy-review-access-form" method="post" onSubmit={sendCode}>
            <div className="strategy-review-access-methods" role="group" aria-label="Contact method">
              {[ ["MOBILE", "Mobile"], ["EMAIL", "Email"] ].map(([value, label]) => (
                <button key={value} type="button" className="strategy-review-access-method" aria-pressed={contactMethod === value} disabled={isBusy} onClick={() => {
                  if (contactMethod === value) return;
                  movedRef.current = true;
                  setContactMethod(value);
                  setContactInput("");
                  setError("");
                  contactRef.current?.focus();
                }}>{label}</button>
              ))}
            </div>
            <label htmlFor={contactId} className="strategy-review-access-label">
              {contactMethod === "MOBILE" ? "Mobile number" : "Email address"}
            </label>
            {contactMethod === "MOBILE" ? (
            <div className="strategy-review-access-mobile" data-invalid={error ? "true" : undefined}>
              <span className="strategy-review-access-country" aria-hidden="true">+1</span>
              <span id={countryId} className="strategy-review-access-sr-only">United States country code +1. Enter a 10-digit mobile number.</span>
              <input
                ref={contactRef}
                id={contactId}
                className="strategy-review-access-input strategy-review-access-input--mobile"
                type="tel"
                inputMode="tel"
                placeholder="(___) ___-____"
                value={contactInput}
                onChange={(event) => setContactInput(event.target.value)}
                onBlur={() => setContactInput((value) => usReviewMobileFieldValue(value))}
                onPaste={(event) => {
                  const pasted = event.clipboardData.getData("text");
                  const input = event.currentTarget;
                  // A whole-number paste replaces its country prefix with the
                  // fixed one. Partial selection pastes retain native editing.
                  if ((!contactInput || (input.selectionStart === 0 && input.selectionEnd === contactInput.length)) && usReviewMobileContact(pasted)) {
                    event.preventDefault();
                    setContactInput(usReviewMobileFieldValue(pasted));
                  }
                }}
                aria-describedby={error ? `${countryId} ${errorId}` : countryId}
                aria-invalid={error ? "true" : undefined}
                autoComplete="tel-national"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={32}
                readOnly={isBusy}
              />
            </div>
            ) : (
              <input
                ref={contactRef}
                id={contactId}
                className="strategy-review-access-input"
                type="text"
                inputMode="email"
                value={contactInput}
                onChange={(event) => setContactInput(event.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? "true" : undefined}
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={STRATEGY_REVIEW_CONTACT_MAX_LENGTH}
                readOnly={isBusy}
              />
            )}
            <button
              type="submit"
              className="strategy-review-access-primary strategy-review-access-submit"
              disabled={isBusy || !contactInput.trim()}
            >
              {busy === "send" ? "Sending…" : "Send code"}
            </button>
          </form>
          {errorLine}
          <div className="strategy-review-access-links">
            <button
              type="button"
              className="strategy-review-access-link"
              onClick={enterExistingCode}
              disabled={isBusy || !contactInput.trim()}
            >
              I already have a code
            </button>
          </div>
        </>
      ) : null}

      {flow.step === STEP.CODE ? (
        <>
          <form className="strategy-review-access-form" method="post" onSubmit={viewStrategy}>
            <label htmlFor={codeId} className="strategy-review-access-label">
              Access code
            </label>
            {/*
              Focus lands here as the line above changes, which can cut a
              polite announcement short; the field carries the line too.
            */}
            <input
              ref={codeRef}
              id={codeId}
              className="strategy-review-access-input strategy-review-access-input--code"
              type="text"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              aria-describedby={error ? `${lineId} ${errorId}` : lineId}
              aria-invalid={error ? "true" : undefined}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={STRATEGY_REVIEW_CODE_MAX_LENGTH}
              readOnly={isBusy}
            />
            <button
              type="submit"
              className="strategy-review-access-primary strategy-review-access-submit"
              disabled={isBusy || !code.trim()}
            >
              {busy === "verify" ? "Checking…" : "View strategy"}
            </button>
            <div className="strategy-review-access-remember">
              <input
                id={rememberId}
                className="strategy-review-access-checkbox"
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                disabled={isBusy}
              />
              <label htmlFor={rememberId}>Remember this device for 30 days</label>
            </div>
          </form>
          {errorLine}
          <div className="strategy-review-access-links">
            {resendReady ? (
              <button
                type="button"
                className="strategy-review-access-link"
                onClick={sendAnotherCode}
                disabled={isBusy}
              >
                {busy === "resend" ? "Sending…" : "Send another code"}
              </button>
            ) : null}
            <button
              type="button"
              className="strategy-review-access-link"
              onClick={changeContact}
              disabled={isBusy}
            >
              Use a different email or mobile
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
